import { palette } from "lib/miolo";
import formatFinancialValue from "components/company/financials/formatFinancialValue";
import FinancialValueThresholdColumnStyles from "components/financials/FinancialValueThresholdColumnStyles";
import TextTooltip from "components/shared/TextTooltip";
import getYear from "date-fns/getYear";
import type { IBrazilianCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/BrazilianCompanyIssuerTopLevel.generated";
import filterUnionByTypename from "lib/utils/filterUnionByTypename";
import parseDateYmd from "lib/utils/parseDateYmd";
import numeral from "numeral";
import classNames from "components/ui/classNames";

interface ITotalCompensationsProps {
  totalCompensations: IBrazilianCompanyIssuerTopLevelFragment["totalCompensations"];
  financials: IBrazilianCompanyIssuerTopLevelFragment["financials"];
}

export default function TotalCompensations({ totalCompensations, financials }: ITotalCompensationsProps) {
  const { yearly } = financials;

  const yearlyBankFinancials = filterUnionByTypename(yearly, "YearlyBrazilianBankFinancialStatementRow");
  const yearlyCompanyFinancials = filterUnionByTypename(yearly, "YearlyBrazilianCompanyFinancialStatementRow");
  const yearlyInsurerFinancials = filterUnionByTypename(yearly, "YearlyBrazilianInsurerFinancialStatementRow");

  const isBank = yearlyBankFinancials.length > 0;
  const isCompany = yearlyCompanyFinancials.length > 0;
  const isInsurer = yearlyInsurerFinancials.length > 0;

  const actualCompensations = totalCompensations
    .reduce<
      {
        fiscalYear: number;

        boardOfDirectors: number;
        boardOfExecutiveOfficers: number;
        fiscalCouncil: number;
        totalCompensation: number;

        netRevenue: number;
        netIncome: number;
      }[]
    >((accumulator, compensation) => {
      if (compensation.totalCompensation === 0) {
        return accumulator;
      }

      const compensationFiscalYearEnd = getYear(parseDateYmd(compensation.fiscalYearEnd));

      if (isBank) {
        const financialStatementRow = yearlyBankFinancials.find(
          ({ endOfPeriod }) => getYear(parseDateYmd(endOfPeriod)) === compensationFiscalYearEnd,
        );

        if (financialStatementRow) {
          return accumulator.concat({
            ...compensation,
            fiscalYear: getYear(parseDateYmd(compensation.fiscalYearEnd)),
            netRevenue: financialStatementRow.financialIntermediationRevenue,
            netIncome: financialStatementRow.netIncome,
          });
        }
      }

      if (isCompany) {
        const financialStatementRow = yearlyCompanyFinancials.find(
          ({ endOfPeriod }) => getYear(parseDateYmd(endOfPeriod)) === compensationFiscalYearEnd,
        );

        if (financialStatementRow) {
          return accumulator.concat({
            ...compensation,
            fiscalYear: getYear(parseDateYmd(compensation.fiscalYearEnd)),
            netRevenue: financialStatementRow.netRevenue,
            netIncome: financialStatementRow.netIncome,
          });
        }
      }

      if (isInsurer) {
        const financialStatementRow = yearlyInsurerFinancials.find(
          ({ endOfPeriod }) => getYear(parseDateYmd(endOfPeriod)) === compensationFiscalYearEnd,
        );

        if (financialStatementRow) {
          return accumulator.concat({
            ...compensation,
            fiscalYear: getYear(parseDateYmd(compensation.fiscalYearEnd)),
            netRevenue: financialStatementRow.netRevenue,
            netIncome: financialStatementRow.netIncome,
          });
        }
      }

      return accumulator;
    }, [])
    .sort((a, b) => {
      return a.fiscalYear - b.fiscalYear;
    });

  const theadClassName = classNames("px-3 py-1 text-sm font-semibold");

  return (
    <table
      className={classNames(
        "min-w-full",
        "[&>thead>tr>th:first-of-type]:rounded-tl",
        "[&>thead>tr>th:last-of-type]:rounded-tr",
        "[&>tbody>tr]:last-of-type:[&>td]:first-of-type:rounded-bl",
        "[&>tbody>tr]:last-of-type:[&>td]:last-of-type:rounded-br",
      )}
    >
      <thead>
        <tr>
          <th
            className={classNames(theadClassName, "text-white")}
            style={{
              backgroundColor: palette.blueBayoux,
              borderRight: `solid 1px ${palette.lynch}`,
            }}
          >
            Ano
          </th>

          <th
            className={theadClassName}
            style={{
              color: palette.blueBayoux,
              backgroundColor: palette.catskillWhite,
              borderRight: `solid 1px ${palette.mystic}`,
            }}
          >
            Diretoria Estatutária
          </th>

          <th
            className={theadClassName}
            style={{
              color: palette.blueBayoux,
              backgroundColor: palette.catskillWhite,
              borderRight: `solid 1px ${palette.mystic}`,
            }}
          >
            Conselho de Administração
          </th>

          <th
            className={theadClassName}
            style={{
              color: palette.blueBayoux,
              backgroundColor: palette.catskillWhite,
              borderRight: `solid 1px ${palette.mystic}`,
            }}
          >
            Conselho Fiscal
          </th>

          <th
            className={theadClassName}
            style={{
              color: palette.scienceBlue,
              backgroundColor: palette.lilyWhite,
            }}
          >
            Total
          </th>

          <th
            className={classNames(theadClassName, "tracking-wide")}
            style={{
              color: palette.seaGreen,
              backgroundColor: palette.grannyApple,
              borderLeft: `solid 1px ${palette.fringyFlower}`,
            }}
          >
            <TextTooltip
              id="tooltips/th/netIncome"
              placement="top"
              content="Receita Líquida - Descontos"
              style={{
                maxWidth: 260,
                textAlign: "center",
              }}
            >
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Lucro Líquido
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className={classNames(theadClassName, "tracking-wide")}
            style={{
              color: palette.luxorGold,
              backgroundColor: palette.linen,
              borderLeft: `solid 1px ${palette.chamois}`,
            }}
          >
            % Lucro
          </th>

          <th
            className={classNames(theadClassName, "tracking-wide")}
            style={{
              color: palette.seaGreen,
              backgroundColor: palette.grannyApple,
              borderLeft: `solid 1px ${palette.fringyFlower}`,
            }}
          >
            <TextTooltip
              id="tooltips/th/netRevenue"
              placement="top"
              content="Receita Líquida"
              style={{
                maxWidth: 260,
                textAlign: "center",
              }}
            >
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Receita
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className={classNames(theadClassName, "tracking-wide")}
            style={{
              color: palette.luxorGold,
              backgroundColor: palette.linen,
              borderLeft: `solid 1px ${palette.chamois}`,
            }}
          >
            % Receita
          </th>
        </tr>
      </thead>
      <tbody
        className={classNames(
          "[&>tr>td]:text-center",
          "[&>tr>td:not(:first-of-type)]:border",
          "[&>tr>td:not(:first-of-type)]:border-t-0",
          "[&>tr>td:not(:first-of-type)]:border-[#dbe2eb]",
          "[&>tr:hover]:bg-slate-50",
          "[&>tr:last-of-type>td]:border-b-0",
        )}
      >
        {actualCompensations.map((compensation) => {
          const baseClassName = "px-3 py-1.5 font-mono text-sm font-semibold";

          return (
            <tr key={compensation.fiscalYear}>
              <td
                className={baseClassName}
                style={{
                  backgroundColor: palette.blueBayoux,
                  color: palette.white,
                  borderTop: `solid 1px ${palette.lynch}`,
                  borderBottom: `solid 1px ${palette.lynch}`,
                  borderRight: `solid 1px ${palette.lynch}`,
                }}
              >
                <span>{compensation.fiscalYear}</span>
              </td>
              <td
                className={baseClassName}
                style={{
                  color: palette.lynch,
                }}
              >
                {numeral(compensation.boardOfExecutiveOfficers).format("0,0").replace(/,/g, ".")}
              </td>
              <td
                className={baseClassName}
                style={{
                  color: palette.lynch,
                }}
              >
                {numeral(compensation.boardOfDirectors).format("0,0").replace(/,/g, ".")}
              </td>
              <td
                className={baseClassName}
                style={{
                  color: palette.lynch,
                }}
              >
                {numeral(compensation.fiscalCouncil).format("0,0").replace(/,/g, ".")}
              </td>
              <td
                className={baseClassName}
                style={{
                  color: palette.scienceBlue,
                }}
              >
                {numeral(compensation.totalCompensation).format("0,0").replace(/,/g, ".")}
              </td>
              <td
                className={baseClassName}
                style={{
                  color: compensation.netIncome >= 0 ? palette.oceanGreen : palette.mojo,
                  border: `solid 1px ${palette.fringyFlower}`,
                  borderRight: "none",
                }}
              >
                {formatFinancialValue(compensation.netIncome)}
              </td>
              <FinancialValueThresholdColumnStyles
                value={
                  compensation.netIncome === 0
                    ? 0
                    : Math.abs((compensation.totalCompensation / compensation.netIncome) * 100)
                }
                comparators={[
                  () => {
                    return compensation.netIncome < 0;
                  },
                  (value) => {
                    return value >= 10;
                  },
                  (value) => {
                    return value < 10 && value > 3;
                  },
                  (value) => {
                    return value <= 3;
                  },
                ]}
                styles={[
                  () => ({
                    color: palette.mojo,
                    backgroundColor: "#FCF1F1",
                  }),
                  () => ({
                    color: palette.mojo,
                    backgroundColor: "#FCF1F1",
                  }),
                  () => ({
                    color: palette.kumera,
                    backgroundColor: palette.butteryWhite,
                    borderBottom: `solid 1px ${palette.chamois}`,
                  }),
                  () => ({
                    color: palette.seaGreen,
                    backgroundColor: "#F1FFF3",
                  }),
                ]}
              >
                {(ratio, style) => {
                  let formattedRatio = `${(ratio || 0).toFixed(2).replace(".", ",")}%`;

                  if ((ratio || 0) >= 10) {
                    formattedRatio = `${Math.round(ratio || 0)}%`;
                  }

                  if ((ratio || 0) < 0 || compensation.netIncome < 0) {
                    formattedRatio = "P";
                  }

                  return (
                    <td
                      className={classNames(baseClassName, "font-medium")}
                      style={{
                        color: palette.luxorGold,
                        border: `solid 1px ${palette.chamois}`,
                        borderRight: "none",
                        ...style,
                      }}
                    >
                      {formattedRatio}
                    </td>
                  );
                }}
              </FinancialValueThresholdColumnStyles>
              <td
                className={baseClassName}
                style={{
                  color: palette.fruitSalad,
                  border: `solid 1px ${palette.fringyFlower}`,
                }}
              >
                {formatFinancialValue(compensation.netRevenue)}
              </td>
              <FinancialValueThresholdColumnStyles
                value={
                  compensation.netRevenue === 0
                    ? 0
                    : Math.abs((compensation.totalCompensation / compensation.netRevenue) * 100)
                }
                comparators={[(value) => value >= 2, (value) => value < 2 && value > 0.5, (value) => value <= 0.5]}
                styles={[
                  () => ({
                    color: palette.mojo,
                    backgroundColor: "#FCF1F1",
                  }),
                  () => ({
                    color: palette.kumera,
                    backgroundColor: palette.butteryWhite,
                    borderBottom: `solid 1px ${palette.chamois}`,
                  }),
                  () => ({
                    color: palette.seaGreen,
                    backgroundColor: "#F1FFF3",
                  }),
                ]}
              >
                {(ratio, style) => (
                  <td
                    className={classNames(baseClassName, "font-medium")}
                    style={{
                      color: palette.luxorGold,
                      border: `solid 1px ${palette.chamois}`,
                      borderRight: "none",
                      ...style,
                    }}
                  >
                    {(ratio || 0) >= 10 ? Math.round(ratio || 0) : (ratio || 0).toFixed(2).replace(".", ",")}%
                  </td>
                )}
              </FinancialValueThresholdColumnStyles>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
